import { api, HttpError } from "../api";
import { refreshPendingBadge } from "../chrome";
import { el } from "../lib/dom";
import { errorBox, heroBand } from "../lib/ui";
import { pendingRequests } from "../session";
import type {
  AccessRequest,
  AdminApp,
  AdminUser,
  AppSummary,
  FleetHealth,
  Grant,
  UserRole,
  WhoAmI,
} from "../types";
import { renderFeedbackTab } from "./feedback_admin";
import { renderInsightsDashboard } from "./insights";

const ROLE_LABEL: Record<UserRole, string> = {
  member: "Member",
  admin: "Admin · all apps",
  super_admin: "Super Admin",
};
const roleOf = (u: AdminUser): UserRole => u.role ?? (u.is_admin ? "super_admin" : "member");
const isBlanket = (u: AdminUser): boolean => roleOf(u) !== "member";

export type AdminTab = "approvals" | "team" | "apps" | "feedback" | "insights" | "health";

const TABS: { key: AdminTab; label: string; href: string }[] = [
  { key: "approvals", label: "Approvals", href: "/admin" },
  { key: "team", label: "Team & access", href: "/admin/team" },
  { key: "apps", label: "Apps", href: "/admin/apps" },
  { key: "feedback", label: "Feedback", href: "/admin/feedback" },
  { key: "insights", label: "Insights", href: "/admin/insights" },
  { key: "health", label: "Fleet health", href: "/admin/health" },
];

/** Best-effort display name from a corporate email: local part split on . _ -
 *  and title-cased. `hemant.shah@bounteous.com` -> `Hemant Shah`. */
function nameFromEmail(email: string): string {
  const local = (email.split("@")[0] || "").trim();
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

/** Sub-tab bar. Approvals carries a live open-request count so it can't be missed. */
function tabNav(active: AdminTab): HTMLElement {
  return el(
    "nav",
    { class: "admin-tabs" },
    TABS.map((t) => {
      const link = el(
        "a",
        { href: t.href, "data-link": "", class: `admin-tab${t.key === active ? " is-active" : ""}` },
        [t.label],
      );
      if (t.key === "approvals" && pendingRequests.get() > 0) {
        link.append(el("span", { class: "nav-badge" }, [String(pendingRequests.get())]));
      }
      return link;
    }),
  );
}

function refreshApprovalsTabBadge(): void {
  const link = document.querySelector<HTMLAnchorElement>('.admin-tabs a[href="/admin"]');
  if (!link) return;
  const n = pendingRequests.get();
  const existing = link.querySelector<HTMLElement>(".nav-badge");
  if (n > 0) {
    if (existing) existing.textContent = String(n);
    else link.append(el("span", { class: "nav-badge" }, [String(n)]));
  } else if (existing) {
    existing.remove();
  }
}

export async function renderAdmin(tab: AdminTab = "approvals"): Promise<HTMLElement> {
  const root = el("main", { class: "page" });
  root.append(
    heroBand("Access", "control", "Review access requests, manage the team, and see usage."),
  );
  root.append(tabNav(tab));
  const body = el("div", { class: "admin-body" });
  root.append(body);

  if (tab === "insights") await renderInsightsTab(body);
  else if (tab === "team") await renderTeamTab(body);
  else if (tab === "apps") await renderAppsTab(body);
  else if (tab === "feedback") await renderFeedbackTab(body);
  else if (tab === "health") await renderHealthTab(body);
  else await renderApprovalsTab(body);
  return root;
}

// ── Approvals ───────────────────────────────────────────────────────────────

async function renderApprovalsTab(body: HTMLElement): Promise<void> {
  body.append(el("h2", { class: "admin-h" }, ["Pending requests"]));
  const reqList = el("div", { class: "req-list" });
  body.append(reqList);

  let requests: AccessRequest[] = [];
  try {
    requests = await api.adminListRequests("pending");
  } catch {
    body.append(errorBox("Couldn't load requests."));
    return;
  }

  // When the request came in + how long it's been waiting (so nothing rots unseen).
  function waited(created: string): string {
    const t = new Date(created);
    if (Number.isNaN(t.getTime())) return "";
    const ms = Math.max(0, Date.now() - t.getTime());
    const d = Math.floor(ms / 86_400_000);
    const h = Math.floor((ms % 86_400_000) / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const ago = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${Math.max(1, m)}m`;
    return `Requested ${t.toLocaleString()} · waiting ${ago}`;
  }

  function requestRow(reqItem: AccessRequest): HTMLElement {
    const status = el("span", { class: "muted-sm" });
    const approve = el("button", { class: "btn btn-primary btn-sm", type: "button" }, ["Approve"]);
    const deny = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Deny"]);
    const node = el("div", { class: "req-row" }, [
      el("div", { class: "req-who" }, [
        el("span", { class: "req-name" }, [reqItem.user_name || reqItem.user_email]),
        el("span", { class: "muted-sm" }, [reqItem.user_email]),
        // Always show SBU + Band so you know who's asking (— when not on file).
        el("span", { class: "req-org" }, [
          `SBU: ${reqItem.user_sub_bu || "—"} · Band: ${reqItem.user_band || "—"}`,
        ]),
        el("span", { class: "muted-sm req-when" }, [waited(reqItem.created_at)]),
      ]),
      el("div", { class: "req-app" }, [reqItem.app_name]),
      el("div", { class: "req-msg" }, [
        el("strong", { class: "req-msg-label" }, ["Reason: "]),
        reqItem.message?.trim() || "— none given —",
      ]),
      el("div", { class: "req-actions" }, [approve, deny, status]),
    ]);

    async function decide(fn: () => Promise<unknown>, label: string) {
      approve.setAttribute("disabled", "true");
      deny.setAttribute("disabled", "true");
      try {
        await fn();
        status.textContent = label;
        node.classList.add("req-decided");
        pendingRequests.set(pendingRequests.get() - 1);
        refreshPendingBadge(); // top nav
        refreshApprovalsTabBadge(); // this tab
      } catch {
        status.textContent = "Failed — retry.";
        approve.removeAttribute("disabled");
        deny.removeAttribute("disabled");
      }
    }
    approve.addEventListener("click", () => void decide(() => api.adminApprove(reqItem.id), "Approved"));

    // Deny collects a note first — it's shown back to the requester so they know why
    // and can re-raise with more detail.
    const actions = node.querySelector(".req-actions") as HTMLElement;
    deny.addEventListener("click", () => {
      const note = el("input", {
        class: "control-input control-mini",
        type: "text",
        placeholder: "Reason for denial (shown to the requester)",
      }) as HTMLInputElement;
      const confirm = el("button", { class: "btn btn-primary btn-sm", type: "button" }, ["Confirm deny"]);
      const cancel = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Cancel"]);
      actions.replaceChildren(note, confirm, cancel, status);
      note.focus();
      cancel.addEventListener("click", () => actions.replaceChildren(approve, deny, status));
      confirm.addEventListener("click", () => {
        confirm.setAttribute("disabled", "true");
        cancel.setAttribute("disabled", "true");
        api
          .adminDeny(reqItem.id, note.value.trim() || undefined)
          .then(() => {
            status.textContent = "Denied";
            node.classList.add("req-decided");
            pendingRequests.set(pendingRequests.get() - 1);
            refreshPendingBadge();
            refreshApprovalsTabBadge();
            actions.replaceChildren(status);
          })
          .catch(() => {
            status.textContent = "Failed — retry.";
            confirm.removeAttribute("disabled");
            cancel.removeAttribute("disabled");
          });
      });
    });
    return node;
  }

  reqList.replaceChildren(
    ...(requests.length
      ? requests.map(requestRow)
      : [el("p", { class: "muted" }, ["No pending requests."])]),
  );

  pendingRequests.set(requests.length);
  refreshPendingBadge();
  refreshApprovalsTabBadge();
}

// ── Team & access — one user-centric table ──────────────────────────────────

async function renderTeamTab(body: HTMLElement): Promise<void> {
  body.append(el("h2", { class: "admin-h" }, ["Team & access"]));
  body.append(
    el("p", { class: "muted-sm" }, [
      "One row per person — their role and exactly which apps they can open. " +
        "Admins can open every app; members see only apps granted to them.",
    ]),
  );

  // Add a user proactively (before their first login) — no permissions yet.
  const newEmail = el("input", { class: "control-input", type: "email", placeholder: "name@bounteous.com" });
  const newName = el("input", { class: "control-input", type: "text", placeholder: "Full name (optional)" });
  const addBtn = el("button", { class: "btn btn-primary btn-sm", type: "button" }, ["Add user"]);
  const addFb = el("span", { class: "muted-sm" });
  body.append(
    el("div", { class: "grant-form" }, [
      el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Email"]), newEmail]),
      el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Name"]), newName]),
      addBtn,
      addFb,
    ]),
  );
  // Auto-fill Name from the email (First Last) until the admin edits it by hand.
  let nameEdited = false;
  newName.addEventListener("input", () => {
    nameEdited = newName.value.trim() !== "";
  });
  newEmail.addEventListener("input", () => {
    if (!nameEdited) newName.value = nameFromEmail(newEmail.value.trim());
  });

  const tableHost = el("div", { class: "team-wrap" });
  body.append(tableHost);

  let users: AdminUser[] = [];
  let apps: AppSummary[] = [];
  let me: WhoAmI;
  let grants: Grant[] = [];
  let adminAppsList: AdminApp[] = [];
  let restrictedKeys = new Set<string>();
  let previewKeys = new Set<string>();
  try {
    [users, apps, me, grants, adminAppsList] = await Promise.all([
      api.adminUsers(),
      api.listApps(),
      api.whoami(),
      api.adminGrants(),
      api.adminApps(),
    ]);
    // Restricted apps are NOT covered by blanket admin access — they need an explicit
    // per-user grant even for Admins/Super-Admins. Track which apps those are.
    restrictedKeys = new Set(adminAppsList.filter((a) => a.restricted).map((a) => a.key));
    // Preview apps (is_visible=false) only ever surface via an explicit grant — same
    // "needs a per-user grant" treatment as restricted, and they're not in `apps`
    // (the visible catalogue), so grantable only from the full admin list.
    previewKeys = new Set(adminAppsList.filter((a) => a.is_visible === false).map((a) => a.key));
  } catch {
    body.append(errorBox("Couldn't load team data."));
    return;
  }

  // email -> the apps that user can open (admins are all-access, never listed here)
  const grantsByEmail = new Map<string, { app_key: string; app_name: string }[]>();
  for (const g of grants) {
    const arr = grantsByEmail.get(g.user_email) ?? [];
    arr.push({ app_key: g.app_key, app_name: g.app_name });
    grantsByEmail.set(g.user_email, arr);
  }
  const appName = (key: string) =>
    apps.find((a) => a.key === key)?.name ?? adminAppsList.find((a) => a.key === key)?.name ?? key;

  // ── Filters (client-side over the already-loaded users + grants) ────────────
  let fltText = "";
  let fltApp = "";
  let fltRole = "";

  // "Who has access to <app>": a member with an explicit grant, plus blanket-tier
  // users (Admin / Super Admin) who can open every non-restricted app.
  const hasApp = (u: AdminUser, appKey: string): boolean => {
    if (!appKey) return true;
    const explicit = (grantsByEmail.get(u.email) ?? []).some((g) => g.app_key === appKey);
    // Blanket (Admin/Super-Admin) covers every NON-restricted app; restricted apps still
    // require an explicit grant, even for admins.
    if (isBlanket(u)) return !restrictedKeys.has(appKey) || explicit;
    return explicit;
  };
  const matches = (u: AdminUser): boolean => {
    const q = fltText.trim().toLowerCase();
    if (q && !(u.email.toLowerCase().includes(q) || (u.name ?? "").toLowerCase().includes(q))) return false;
    if (fltRole && roleOf(u) !== fltRole) return false;
    return hasApp(u, fltApp);
  };

  const searchInput = el("input", { class: "control-input", type: "search", placeholder: "Name or email" }) as HTMLInputElement;
  searchInput.addEventListener("input", () => { fltText = searchInput.value; paint(); });
  const appSel = el("select", { class: "control-input control-mini" }, [
    el("option", { value: "" }, ["Any app"]),
    ...apps.map((a) => el("option", { value: a.key }, [a.name])),
  ]) as HTMLSelectElement;
  appSel.addEventListener("change", () => { fltApp = appSel.value; paint(); });
  const roleSel = el("select", { class: "control-input control-mini" }, [
    el("option", { value: "" }, ["Any role"]),
    el("option", { value: "member" }, ["Members"]),
    el("option", { value: "admin" }, ["Admins"]),
    el("option", { value: "super_admin" }, ["Super Admins"]),
  ]) as HTMLSelectElement;
  roleSel.addEventListener("change", () => { fltRole = roleSel.value; paint(); });
  const countFb = el("span", { class: "muted-sm team-count" });
  const filterBar = el("div", { class: "grant-form team-filters" }, [
    el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Search"]), searchInput]),
    el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Has access to"]), appSel]),
    el("label", { class: "control" }, [el("span", { class: "eyebrow" }, ["Role"]), roleSel]),
    countFb,
  ]);
  tableHost.before(filterBar);

  function accessCell(u: AdminUser): HTMLElement {
    const blanket = isBlanket(u);
    const granted = grantsByEmail.get(u.email) ?? [];
    // Admins get every NON-restricted PUBLISHED app via blanket, so here we only show +
    // grant what blanket does NOT cover — restricted apps and Preview apps. Members: all.
    const shown = blanket
      ? granted.filter((g) => restrictedKeys.has(g.app_key) || previewKeys.has(g.app_key))
      : granted;
    const chips = shown.map((g) => {
      const x = el(
        "button",
        { class: "chip-x", type: "button", title: `Revoke ${g.app_name}`, "aria-label": `Revoke ${g.app_name}` },
        ["✕"],
      );
      x.addEventListener("click", async () => {
        x.setAttribute("disabled", "true");
        try {
          await api.adminRevoke(g.app_key, u.email);
          grantsByEmail.set(
            u.email,
            (grantsByEmail.get(u.email) ?? []).filter((a) => a.app_key !== g.app_key),
          );
          paint();
        } catch {
          x.removeAttribute("disabled");
        }
      });
      return el("span", { class: "chip" }, [g.app_name, x]);
    });

    // Members can be granted any app (incl. Preview apps, which aren't in the visible
    // catalogue `apps`); admins only what blanket doesn't cover — restricted + Preview.
    const pool = (
      blanket
        ? adminAppsList.filter((a) => a.restricted || a.is_visible === false)
        : adminAppsList
    ) as { key: string; name: string }[];
    const ungranted = pool.filter((a) => !granted.some((g) => g.app_key === a.key));
    const sel = el("select", { class: "control-input control-mini" }, [
      el("option", { value: "" }, [blanket ? "＋ Grant restricted app" : "＋ Grant app"]),
      ...ungranted.map((a) => el("option", { value: a.key }, [a.name])),
    ]) as HTMLSelectElement;
    sel.addEventListener("change", async () => {
      const key = sel.value;
      if (!key) return;
      sel.setAttribute("disabled", "true");
      try {
        await api.adminGrant(key, u.email);
        const arr = grantsByEmail.get(u.email) ?? [];
        arr.push({ app_key: key, app_name: appName(key) });
        grantsByEmail.set(u.email, arr);
        paint();
      } catch {
        sel.removeAttribute("disabled");
      }
    });

    // Admins: "All apps" (the non-restricted set via blanket) + any restricted grants.
    const prefix = blanket ? [el("span", { class: "muted-sm" }, ["All apps"])] : [];
    const noneLabel =
      !blanket && chips.length === 0 ? [el("span", { class: "muted-sm" }, ["— none —"])] : [];
    return el("td", {}, [
      el("div", { class: "chips" }, [...prefix, ...chips, ...noneLabel, sel]),
    ]);
  }

  function roleCell(u: AdminUser): HTMLElement {
    const isSelf = u.email === me.email;
    const current = roleOf(u);
    const sel = el(
      "select",
      { class: "control-input control-mini" },
      (["member", "admin", "super_admin"] as UserRole[]).map((r) =>
        el("option", { value: r }, [ROLE_LABEL[r]]),
      ),
    ) as HTMLSelectElement;
    sel.value = current;
    if (isSelf) sel.setAttribute("disabled", "true"); // can't change your own role
    sel.addEventListener("change", async () => {
      const role = sel.value as UserRole;
      sel.setAttribute("disabled", "true");
      try {
        const updated = await api.adminSetUserRole(u.email, role);
        u.role = updated.role;
        u.is_admin = updated.is_admin;
        if (role !== "member") grantsByEmail.delete(u.email); // blanket tiers: grants cleared
        paint();
      } catch {
        sel.value = current;
        if (!isSelf) sel.removeAttribute("disabled");
      }
    });
    return el("td", {}, [
      el("div", { class: "role-cell" }, [isSelf ? el("span", { class: "muted-sm" }, ["You"]) : null, sel]),
    ]);
  }

  function row(u: AdminUser): HTMLElement {
    const isSelf = u.email === me.email;
    const remove = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Remove"]);
    if (isSelf) remove.setAttribute("disabled", "true");
    remove.addEventListener("click", async () => {
      if (!window.confirm(`Remove ${u.email}? This deletes their access, requests and favourites.`)) return;
      remove.setAttribute("disabled", "true");
      try {
        await api.adminDeleteUser(u.email);
        users = users.filter((x) => x.email !== u.email);
        grantsByEmail.delete(u.email);
        paint();
      } catch {
        remove.removeAttribute("disabled");
      }
    });
    return el("tr", {}, [
      el("td", {}, [
        el("div", { class: "req-who" }, [
          el("span", { class: "req-name" }, [u.name || u.email]),
          el("span", { class: "muted-sm" }, [isSelf ? `${u.email} · you` : u.email]),
        ]),
      ]),
      roleCell(u),
      accessCell(u),
      el("td", {}, [remove]),
    ]);
  }

  function paint(): void {
    const shown = users.filter(matches);
    countFb.textContent = `${shown.length} of ${users.length} ${users.length === 1 ? "person" : "people"}`;
    tableHost.replaceChildren(
      el("table", { class: "team-table" }, [
        el("thead", {}, [
          el("tr", {}, [
            el("th", {}, ["User"]),
            el("th", {}, ["Role"]),
            el("th", {}, ["App access"]),
            el("th", {}, [""]),
          ]),
        ]),
        el(
          "tbody",
          {},
          shown.length
            ? shown.map(row)
            : [
                el("tr", {}, [
                  el("td", { colspan: "4", class: "muted-sm" }, [
                    users.length ? "No people match these filters." : "No users yet.",
                  ]),
                ]),
              ],
        ),
      ]),
    );
  }

  addBtn.addEventListener("click", async () => {
    const email = newEmail.value.trim();
    if (!email) {
      addFb.textContent = "Enter an email.";
      return;
    }
    addBtn.setAttribute("disabled", "true");
    addFb.textContent = "";
    try {
      const created = await api.adminCreateUser(email, newName.value.trim() || undefined);
      users = [...users, created].sort((a, b) => a.email.localeCompare(b.email));
      newEmail.value = "";
      newName.value = "";
      addFb.textContent = `Added ${created.email}.`;
      paint();
    } catch (e) {
      addFb.textContent =
        e instanceof HttpError && e.status === 409
          ? "That user already exists."
          : e instanceof HttpError && e.status === 400
            ? "That doesn't look like a valid email."
            : "Couldn't add user — try again.";
    } finally {
      addBtn.removeAttribute("disabled");
    }
  });

  paint();
}

// ── Apps — per-app settings (global access) ──────────────────────────────────

async function renderAppsTab(body: HTMLElement): Promise<void> {
  body.append(el("h2", { class: "admin-h" }, ["Apps"]));
  body.append(
    el("p", { class: "muted-sm" }, [
      'Tick "Everyone can access" to open an app to all signed-in users — direct ' +
        "launch, no request/approve, and Hub won't provision it. Untick to return to " +
        "the request/grant model; existing per-user grants are preserved and re-apply.",
    ]),
  );
  let apps: AdminApp[];
  let hub: { on: boolean; message: string | null } = { on: false, message: null };
  try {
    apps = await api.adminApps();
    hub = (await api.getMaintenance()).hub;
  } catch {
    body.append(errorBox("Couldn't load apps."));
    return;
  }

  // ── Hub-level maintenance ──
  const hubToggle = el("input", { type: "checkbox", class: "ga-toggle" }) as HTMLInputElement;
  hubToggle.checked = hub.on;
  const hubMsg = el("input", {
    class: "control-input",
    type: "text",
    placeholder: "Message shown on the maintenance page",
    value: hub.message ?? "",
  }) as HTMLInputElement;
  const hubFb = el("span", { class: "muted-sm" });
  async function saveHub() {
    hubToggle.disabled = true;
    hubFb.textContent = "Saving…";
    try {
      const r = await api.adminSetHubMaintenance(hubToggle.checked, hubMsg.value.trim() || undefined);
      hubFb.textContent = r.on
        ? "Hub maintenance ON — only super-admins can use Hub."
        : "Hub maintenance off.";
    } catch {
      hubToggle.checked = !hubToggle.checked;
      hubFb.textContent = "Failed — retry.";
    } finally {
      hubToggle.disabled = false;
    }
  }
  hubToggle.addEventListener("change", () => void saveHub());
  const saveHubBtn = el("button", { class: "btn btn-ghost btn-sm", type: "button" }, ["Save message"]);
  saveHubBtn.addEventListener("click", () => void saveHub());
  body.append(
    el("div", { class: "maintenance-panel" }, [
      el("label", { class: "ga-label" }, [hubToggle, el("span", {}, ["Hub maintenance mode"])]),
      hubMsg,
      saveHubBtn,
      hubFb,
    ]),
  );

  const tableHost = el("div", { class: "team-wrap" });
  body.append(tableHost);

  function accessToggle(
    a: AdminApp,
    field: "global_access" | "restricted",
    label: string,
    call: (key: string, v: boolean) => Promise<AdminApp>,
  ): HTMLElement {
    const toggle = el("input", { type: "checkbox", class: "ga-toggle" }) as HTMLInputElement;
    toggle.checked = a[field];
    toggle.addEventListener("change", () => {
      const next = toggle.checked;
      toggle.disabled = true;
      call(a.key, next)
        .then((updated) => {
          a.global_access = updated.global_access;
          a.restricted = updated.restricted;
          toggle.disabled = false;
          paint();
        })
        .catch(() => {
          toggle.checked = !next; // revert — never lie about server state
          toggle.disabled = false;
        });
    });
    return el("label", { class: "ga-label" }, [toggle, el("span", {}, [label])]);
  }

  function modeCell(a: AdminApp): HTMLElement {
    if (a.global_access) return el("span", { class: "badge-ok" }, ["open to all"]);
    if (a.restricted) return el("span", { class: "badge-fail" }, ["explicit only"]);
    return el("span", { class: "muted-sm" }, [
      a.provision_url ? "request / grant · provisions" : "request / grant",
    ]);
  }

  function maintToggle(a: AdminApp): HTMLElement {
    const t = el("input", { type: "checkbox", class: "ga-toggle" }) as HTMLInputElement;
    t.checked = !!a.maintenance;
    t.addEventListener("change", () => {
      const next = t.checked;
      t.disabled = true;
      api
        .adminSetAppMaintenance(a.key, next)
        .then((u) => {
          a.maintenance = u.maintenance;
          t.disabled = false;
          paint();
        })
        .catch(() => {
          t.checked = !next;
          t.disabled = false;
        });
    });
    return el("label", { class: "ga-label" }, [
      t,
      el("span", {}, [a.maintenance ? "under maintenance" : "live"]),
    ]);
  }

  // Governed access: Self-service on = members can request in-system; off = they see the
  // note ("email your MD…") and grants are admin-only.
  function policyCell(a: AdminApp): HTMLElement {
    const toggle = el("input", { type: "checkbox", class: "ga-toggle" }) as HTMLInputElement;
    toggle.checked = a.self_request !== false;
    const note = el("input", {
      class: "control-input control-mini",
      type: "text",
      placeholder: "Message shown to users (e.g. email your MD)",
      value: a.access_note ?? "",
    }) as HTMLInputElement;
    const save = (selfReq: boolean, accessNote: string) => {
      toggle.disabled = true;
      note.disabled = true;
      api
        .adminSetAccessPolicy(a.key, selfReq, accessNote || undefined)
        .then((u) => {
          a.self_request = u.self_request;
          a.access_note = u.access_note;
          paint();
        })
        .catch(() => {
          toggle.checked = a.self_request !== false;
          toggle.disabled = false;
          note.disabled = false;
        });
    };
    toggle.addEventListener("change", () => save(toggle.checked, note.value.trim()));
    note.addEventListener("change", () => save(toggle.checked, note.value.trim()));
    const wrap = el("div", { class: "policy-cell" }, [
      el("label", { class: "ga-label" }, [toggle, el("span", {}, ["Self-service"])]),
    ]);
    if (!toggle.checked) wrap.append(note); // note only matters when governed
    return wrap;
  }

  function row(a: AdminApp): HTMLElement {
    return el("tr", {}, [
      el("td", {}, [el("strong", {}, [a.name]), el("div", { class: "muted-sm" }, [a.key])]),
      el("td", {}, [a.app_type]),
      el("td", {}, [accessToggle(a, "global_access", "Everyone can access", api.adminSetGlobalAccess)]),
      el("td", {}, [accessToggle(a, "restricted", "Explicit only", api.adminSetRestricted)]),
      el("td", {}, [policyCell(a)]),
      el("td", {}, [maintToggle(a)]),
      el("td", {}, [modeCell(a)]),
    ]);
  }

  function paint(): void {
    tableHost.replaceChildren(
      el("table", { class: "team-table" }, [
        el("thead", {}, [
          el(
            "tr",
            {},
            ["App", "Type", "Global access", "Restricted", "Requests", "Maintenance", "Mode"].map(
              (h) => el("th", {}, [h]),
            ),
          ),
        ]),
        el("tbody", {}, apps.map(row)),
      ]),
    );
  }
  paint();
}

// ── Insights & Audit (usage + audit trail dashboard) ─────────────────────────

async function renderInsightsTab(body: HTMLElement): Promise<void> {
  await renderInsightsDashboard(body);
}

// ── Fleet health (shared-box vitals from the on-box probe) ───────────────────

async function renderHealthTab(body: HTMLElement): Promise<void> {
  body.append(el("h2", { class: "admin-h" }, ["Fleet health"]));
  body.append(
    el("p", { class: "muted-sm" }, [
      "Vitals for the shared box the fleet runs on. The on-box probe reports every few " +
        "minutes; super-admins are emailed on a threshold breach.",
    ]),
  );

  let fh: FleetHealth;
  try {
    fh = await api.adminFleetHealth();
  } catch {
    body.append(errorBox("Couldn't load fleet health."));
    return;
  }

  if (!fh.host || fh.updated_at === null) {
    body.append(
      el("div", { class: "health-empty" }, [
        "No health report yet — the on-box probe hasn't reported. Vitals appear here once " +
          "the monitor cron is installed on the box.",
      ]),
    );
    return;
  }

  if (fh.breaches.length) {
    body.append(
      el("div", { class: "health-breach" }, [
        el("strong", {}, ["⚠️ Active issues"]),
        el(
          "ul",
          {},
          fh.breaches.map((b) => el("li", {}, [b])),
        ),
      ]),
    );
  }
  if (fh.stale) {
    body.append(
      el("div", { class: "health-stale" }, [
        "⚠️ Stale — no report in the last 12 minutes (the probe or the box may be down).",
      ]),
    );
  }

  const h = fh.host;
  const c = fh.containers;
  const tiles: [string, string, boolean][] = [
    ["Load", `${h.load1.toFixed(2)} / ${h.cores}`, h.load1 > h.cores * 1.5],
    ["Memory free", `${h.mem_avail_gib.toFixed(1)} GiB`, h.mem_avail_gib < 1.5],
    [
      "Swap used",
      `${h.swap_used_gib.toFixed(1)} / ${h.swap_total_gib.toFixed(0)} GiB`,
      h.swap_total_gib > 0 && h.swap_used_gib > 4,
    ],
    ["Disk", `${h.disk_pct.toFixed(0)}%`, h.disk_pct >= 80],
    ["Containers", c ? `${c.up} / ${c.total} up` : "—", !!(c && (c.down || c.restarting))],
  ];
  body.append(
    el(
      "div",
      { class: "health-tiles" },
      tiles.map(([label, val, bad]) =>
        el("div", { class: `health-tile${bad ? " is-bad" : ""}` }, [
          el("div", { class: "ht-val" }, [val]),
          el("div", { class: "ht-label" }, [label]),
        ]),
      ),
    ),
  );

  if (c && c.problem.length) {
    body.append(
      el("div", { class: "health-problems" }, [
        el("h3", {}, ["Containers needing attention"]),
        el(
          "ul",
          {},
          c.problem.map((p) => el("li", {}, [`${p.name ?? "?"} — ${p.status ?? ""}`])),
        ),
      ]),
    );
  }

  const when = new Date(fh.updated_at).toLocaleString();
  body.append(el("p", { class: "muted-sm" }, [`Last report: ${when}`]));
}
